import classNames from "classnames";
import { VideoPlayer } from "components/VideoPlayer/VideoPlayer";
import { ESCAPE_HTML_COMPONENT, MEDIA_TYPE, ORGANIZATIONS } from "constant";
import { useTranslation } from "react-i18next";
import { storage } from "services/storage";
import { useLanguageContext } from "store/LanguageProvider";
import { parseAndEscapeCode } from "utils/helper";

export const Option = ({
  id,
  text,
  type,
  mediaUrl,
  embedUrl,
  onOptionClick,
  selected,
  isOptionTextLong = () => { },
  isQuesMCQWithImageOptions,
  isMcq = false,
  spotlightOrgFlag = false,
  isOptionUpperCase = false,
  correct = false,
  weight = 0,
}) => {
  const organization = storage.get.org();
  const isParentCC = storage.get.parentApp() === "CC";
  const wizrFlag = organization === ORGANIZATIONS.WIZR;
  const isHtml = /<\/?[a-z][\s\S]*>/i.test(text);

  const { rtlView, direction } = useLanguageContext();
  const { t } = useTranslation()

  // Additional check to filter question having only open tags like <h1>, <div>, <button>, <input>
  // Otherwise these are rendered as html elements -> Which is not the intention for such questions.
  const htmlHasClosingTags = /<\/[^<>]*>/g.test(text);

  const optionClass = classNames(
    `rounded-[10px] border border-stark-white py-3 px-[15px] tracking-[-0.5px] transition cursor-pointer whitespace-pre-line`,
    {
      // Hover/active states render text as white -> Segregate when text is isHTML
      'sm:hover:bg-buddha-gold active:bg-buddha-gold': isHtml || !htmlHasClosingTags,
      'sm:hover:bg-buddha-gold sm:hover:text-wizr-black active:bg-buddha-gold active:text-white': !isHtml
    },
    {
      "text-left font-medium text-base leading-[22px] tracking-[0.1px] sm:text-base":
        type === "text",
      "text-center font-semibold text-lg leading-[26px] tracking-[-0.5px]":
        type === "text" && (typeof text === "number") && !(isMcq && spotlightOrgFlag),
    },
    {
      "bg-wizr-grey text-wizr-black active:bg-wizr-green active:text-wizr-black": wizrFlag,
      // "sm:active:bg-wizr-green sm:active:text-wizr-black sm:hover:bg-wizr-green sm:hover:text-wizr-black": wizrFlag
    },
    {
      "bg-fog-tint": !selected,
      "bg-cocoa-brown text-white": selected && !(isMcq && spotlightOrgFlag),
      "!bg-wizr-green text-wizr-black": wizrFlag && selected,
    },
    {
      "w-[95%] rounded-[0] border-[#fff]  font-extralight break-words text-center": isMcq && spotlightOrgFlag,
      "bg-[#fff] text-[#000]": isMcq && spotlightOrgFlag && selected,
      "text-[#fff] bg-[#1C1E1F]": isMcq && spotlightOrgFlag && !selected,
      "uppercase": isOptionUpperCase
    },
    {
      "!text-right-ar-small": rtlView
    }
  );

  const correctOptionClass = classNames({
    "border-2 border-lime-400": isParentCC && correct,
  })

  const optionWeightClass = classNames({
    "hidden": !(isParentCC && weight),
    "bg-fog-tint rounded-full w-12 h-12 flex items-center justify-center text-[12px] font-light mr-2": isParentCC && weight,
  })

  switch (type) {
    case MEDIA_TYPE.IMAGE:
      return (
        <div
          className={classNames("cursor-pointer flex items-center", correctOptionClass)}
          onClick={() => onOptionClick({ optionId: id })}
        >
          <div title="Weightage" className={optionWeightClass}>{weight}</div>
          <img
            src={mediaUrl}
            alt=""
            className={`rounded-lg flex-1 ${isQuesMCQWithImageOptions ? `p-[10px] border border-stone-700 ${selected ? "bg-buddha-gold" : "bg-stone-100"}` : ""}`}
          />
        </div>
      );

    case MEDIA_TYPE.VIDEO:
      return (
        <div className={classNames("flex items-center")}>
          <div title="Weightage" className={optionWeightClass}>{weight}</div>
          <video
            loop
            autoPlay
            controls
            muted
            onClick={() => onOptionClick({ optionId: id })}
            className={classNames("max-h-40 w-full cursor-pointer flex-1", correctOptionClass)}
          >
            <source src={mediaUrl} />
            {t("embed_no_support")}
          </video>
        </div>
      );

    case MEDIA_TYPE.EMBED:
      return (
        <div className={classNames("flex items-center")}>
          <div title="Weightage" className={optionWeightClass}>{weight}</div>
          <VideoPlayer
            src={embedUrl}
            className={classNames("border-1 cursor-pointer rounded-lg border p-2 transition-all hover:border-cocoa-brown flex-1", correctOptionClass)}
            onClick={() => onOptionClick({ optionId: id })}
          />
        </div>
      );

    default:
      return (
        <div className={classNames("flex items-center")}>
          <div title="Weightage" className={optionWeightClass}>{weight}</div>
          {
            isHtml && htmlHasClosingTags ? (
              <div
                className={classNames(optionClass, "html options-html flex-1", correctOptionClass)}
                onClick={() => onOptionClick({ optionId: id })}
                dangerouslySetInnerHTML={{ __html: isHtml ? parseAndEscapeCode(text, ESCAPE_HTML_COMPONENT.OPTION) : null }}
              />
            ) : (
              <div
                className={classNames(optionClass, correctOptionClass, "flex-1")}
                onClick={() => onOptionClick({ optionId: id })}
              >
                {text}
              </div>
            )
          }
        </div>

      );
  }
};
